/** Admin flow management page with credential assignment. */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, IconButton, Chip, Switch, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Alert, Tooltip,
  List, ListItem, ListItemText, ListItemSecondaryAction, Select, MenuItem, FormControl, InputLabel,
  TableSortLabel, Autocomplete, TablePagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DeleteIcon from "@mui/icons-material/Delete";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import EditIcon from "@mui/icons-material/Edit";
import AutoStoriesOutlined from "@mui/icons-material/AutoStoriesOutlined";
import {
  useAdminPlugins, useUploadPlugin, useTogglePluginStatus, useUpdatePluginCode, useDeletePlugin, usePluginCode,
  useUpdatePluginMetadata,
  useAdminCredentials, usePluginCredentials, useAssignCredential, useUnassignCredential,
} from "@/hooks/useAdmin";
import type { Plugin } from "@/types";
import KnowledgeSourcePanel from "@/components/KnowledgeSourcePanel";

type SortKey = "name" | "category" | "status" | "secure";
type SortDir = "asc" | "desc";

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const DEFAULT_PAGE_SIZE = 10;

export default function PluginManagement() {
  const navigate = useNavigate();

  // Server-side cursor pagination. DynamoDB nextToken cursors are forward-only,
  // so we keep a stack of the cursors used to reach each page: cursorStack[i]
  // is the nextToken that loads page i (page 0 uses `undefined`). Paging back
  // pops the stack; changing page size resets to the first page.
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([undefined]);
  const pageIndex = cursorStack.length - 1;
  const currentCursor = cursorStack[pageIndex];

  const { data, isLoading, isFetching } = useAdminPlugins(pageSize, currentCursor);
  const nextToken = data?.nextToken ?? null;

  const handleNextPage = () => {
    if (nextToken) setCursorStack((s) => [...s, nextToken]);
  };
  const handlePrevPage = () => {
    setCursorStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  };
  const handleChangePageSize = (size: number) => {
    setPageSize(size);
    setCursorStack([undefined]); // reset to first page — cursors are size-specific
  };

  const uploadPlugin = useUploadPlugin();
  const toggleStatus = useTogglePluginStatus();
  const deletePlugin = useDeletePlugin();
  const assignCred = useAssignCredential();
  const { data: allCredsData } = useAdminCredentials();
  const [dialog, setDialog] = useState<"upload" | "editPlugin" | "creds" | "confirmDelete" | null>(null);
  const [form, setForm] = useState({ name: "", description: "", category: "reporting", secure: false, timeout: 300, source: "", pluginId: "", credentialIds: [] as string[] });
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [deleteTarget, setDeleteTarget] = useState<Plugin | null>(null);
  const [knowledgePlugin, setKnowledgePlugin] = useState<Plugin | null>(null);

  const handleSort = (key: SortKey) => {
    setSortDir(sortKey === key && sortDir === "asc" ? "desc" : "asc");
    setSortKey(key);
  };

  const plugins = useMemo(() => {
    const list = [...(data?.items ?? [])];
    list.sort((a, b) => {
      const av = String(a[sortKey] ?? "");
      const bv = String(b[sortKey] ?? "");
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return list;
  }, [data?.items, sortKey, sortDir]);

  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleUpload = () => {
    const selectedCredIds = form.credentialIds;
    uploadPlugin.mutate({ ...form }, { onSuccess: async (result: { pluginId?: string }) => {
      const newPluginId = result?.pluginId;
      if (newPluginId && selectedCredIds.length > 0) {
        await Promise.all(
          selectedCredIds.map((credentialId) =>
            assignCred.mutateAsync({ pluginId: newPluginId, credentialId })
          )
        );
      }
      setUploadSuccess(true);
      setCursorStack([undefined]); // surface the newly uploaded flow on page one
      setTimeout(() => {
        setUploadSuccess(false);
        setDialog(null);
        setForm({ name: "", description: "", category: "reporting", secure: false, timeout: 300, source: "", pluginId: "", credentialIds: [] });
      }, 1200);
    } });
  };

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      deletePlugin.mutate(deleteTarget.pluginId, { onSuccess: () => {
        setDeleteTarget(null);
        setDialog(null);
        // Deleting can leave the current cursor pointing past the end; reset.
        setCursorStack([undefined]);
      } });
    }
  };

  const handleEditAction = (p: Plugin) => {
    if (p.pluginType === "flow") {
      navigate(`/admin/flow-builder?pluginId=${p.pluginId}`);
    } else {
      setForm((f) => ({ ...f, pluginId: p.pluginId, name: p.name, description: p.description, category: p.category }));
      setDialog("editPlugin");
    }
  };

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  const sortLabel = (key: SortKey, label: string) => (
    <TableSortLabel active={sortKey === key} direction={sortKey === key ? sortDir : "asc"} onClick={() => handleSort(key)}>
      {label}
    </TableSortLabel>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Flow Management</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button startIcon={<AccountTreeIcon />} variant="outlined" onClick={() => navigate("/admin/flow-builder")}>Build Flow</Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setDialog("upload")}>Upload Flow</Button>
        </Box>
      </Box>

      {uploadPlugin.isError && <Alert severity="error" sx={{ mb: 2 }}>{uploadPlugin.error.message}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{sortLabel("name", "Name")}</TableCell>
              <TableCell>{sortLabel("category", "Category")}</TableCell>
              <TableCell>{sortLabel("status", "Status")}</TableCell>
              <TableCell>{sortLabel("secure", "Secure")}</TableCell>
              <TableCell>Credentials</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plugins.map((p) => (
              <TableRow key={p.pluginId} hover>
                <TableCell>{p.name}</TableCell>
                <TableCell><Chip label={p.category} size="small" /></TableCell>
                <TableCell><Chip label={p.status} size="small" color={p.status === "active" ? "success" : "default"} /></TableCell>
                <TableCell>{p.secure ? "Yes" : "No"}</TableCell>
                <TableCell>
                  {p.pluginType !== "flow" && p.hasCredentials !== undefined && (
                    <Tooltip title={p.hasCredentials ? "Credentials configured" : "No credentials assigned"}>
                      <VpnKeyIcon fontSize="small" color={p.hasCredentials ? "success" : "error"} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={p.status === "active" ? "Disable flow" : "Enable flow"}><Switch size="small" checked={p.status === "active"} onChange={() => toggleStatus.mutate(p.pluginId)} /></Tooltip>
                  <Tooltip title="Edit metadata"><IconButton size="small" onClick={() => handleEditAction(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Knowledge base"><IconButton size="small" onClick={() => setKnowledgePlugin(p)} aria-label="Knowledge base"><AutoStoriesOutlined fontSize="small" /></IconButton></Tooltip>
                  {p.pluginType === "python" && (
                    <Tooltip title="Manage credentials"><IconButton size="small" onClick={() => { setForm((f) => ({ ...f, pluginId: p.pluginId })); setDialog("creds"); }}><VpnKeyIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                  <Tooltip title="Delete flow"><IconButton size="small" onClick={() => { setDeleteTarget(p); setDialog("confirmDelete"); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {plugins.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography color="text.secondary">No flows found.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          // Total count is unknown with cursor pagination; -1 hides the "of N".
          count={-1}
          page={pageIndex}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
          onPageChange={(_e, newPage) => {
            if (newPage > pageIndex) handleNextPage();
            else handlePrevPage();
          }}
          onRowsPerPageChange={(e) => handleChangePageSize(parseInt(e.target.value, 10))}
          labelDisplayedRows={() => {
            const first = pageIndex * pageSize + 1;
            const last = pageIndex * pageSize + plugins.length;
            return plugins.length === 0 ? "0" : `${first}–${last}`;
          }}
          slotProps={{
            actions: {
              nextButton: {
                "aria-label": "next page",
                disabled: !nextToken || isFetching,
              },
              previousButton: {
                "aria-label": "previous page",
                disabled: pageIndex === 0 || isFetching,
              },
            },
          }}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialog === "confirmDelete"} onClose={() => { setDeleteTarget(null); setDialog(null); }}>
        <DialogTitle>Permanently Delete Flow?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete <strong>{deleteTarget?.name}</strong> including all credential assignments and flow code. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteTarget(null); setDialog(null); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={dialog === "upload"} onClose={() => setDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Flow</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          {uploadSuccess && <Alert severity="success">Flow uploaded and credentials assigned successfully!</Alert>}
          <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} size="small" />
          <TextField label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} size="small" />
          <TextField label="Category" select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} size="small" SelectProps={{ native: true }}>
            <option value="accelo">Accelo</option><option value="zendesk">Zendesk</option><option value="datadog">Datadog</option><option value="reporting">Reporting</option><option value="analysis">Analysis</option>
          </TextField>
          <TextField label="Flow Source Code" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} multiline rows={12} fullWidth sx={{ fontFamily: "monospace" }} />
          <Autocomplete
            multiple
            size="small"
            options={allCredsData?.items ?? []}
            getOptionLabel={(option) => `${option.name} (${option.credentialType})`}
            value={(allCredsData?.items ?? []).filter((c) => form.credentialIds.includes(c.credentialId))}
            onChange={(_e, newValue) => setForm((f) => ({ ...f, credentialIds: newValue.map((c) => c.credentialId) }))}
            isOptionEqualToValue={(option, value) => option.credentialId === value.credentialId}
            renderInput={(params) => <TextField {...params} label="Credentials" placeholder="Select credentials" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!form.source || !form.name}>Upload</Button>
        </DialogActions>
      </Dialog>

      {/* Credentials Dialog */}
      {dialog === "creds" && <CredentialsDialog pluginId={form.pluginId} onClose={() => setDialog(null)} />}

      {/* Edit Plugin Dialog (for python/undefined pluginType) */}
      {dialog === "editPlugin" && <EditPluginDialog pluginId={form.pluginId} name={form.name} description={form.description} category={form.category} onClose={() => setDialog(null)} />}

      {/* Knowledge Base Dialog */}
      <Dialog open={!!knowledgePlugin} onClose={() => setKnowledgePlugin(null)} maxWidth="md" fullWidth>
        <DialogTitle>Knowledge Base - {knowledgePlugin?.name}</DialogTitle>
        <DialogContent>
          {knowledgePlugin && <KnowledgeSourcePanel pluginId={knowledgePlugin.pluginId} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKnowledgePlugin(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function EditPluginDialog({ pluginId, name, description, category, onClose }: { pluginId: string; name: string; description: string; category: string; onClose: () => void }) {
  const updateMeta = useUpdatePluginMetadata();
  const updateCode = useUpdatePluginCode();
  const { data: codeData, isLoading: codeLoading } = usePluginCode(pluginId);
  const { data: assigned, isLoading: credsLoading } = usePluginCredentials(pluginId);
  const { data: allCreds } = useAdminCredentials();
  const assignCred = useAssignCredential();
  const unassignCred = useUnassignCredential();
  const [desc, setDesc] = useState(description);
  const [cat, setCat] = useState(category);
  const [source, setSource] = useState("");
  const [sourceInitialized, setSourceInitialized] = useState(false);
  const [selectedCred, setSelectedCred] = useState("");

  if (codeData?.source && !sourceInitialized) {
    setSource(codeData.source);
    setSourceInitialized(true);
  }

  const assignedIds = new Set((assigned?.items ?? []).map((i) => i.credentialId));
  const available = (allCreds?.items ?? []).filter((c) => !assignedIds.has(c.credentialId));

  const handleSave = () => {
    const metaPromise = updateMeta.mutateAsync({ pluginId, description: desc, category: cat });
    const codePromise = source ? updateCode.mutateAsync({ pluginId, source }) : Promise.resolve();
    Promise.all([metaPromise, codePromise]).then(() => onClose());
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Flow</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <TextField label="Name" value={name} size="small" fullWidth disabled />
        <TextField label="Description" value={desc} onChange={(e) => setDesc(e.target.value)} size="small" multiline rows={3} fullWidth />
        <TextField label="Category" select value={cat} onChange={(e) => setCat(e.target.value)} size="small" SelectProps={{ native: true }}>
          <option value="accelo">Accelo</option><option value="zendesk">Zendesk</option><option value="datadog">Datadog</option><option value="reporting">Reporting</option><option value="analysis">Analysis</option>
        </TextField>
        {codeLoading ? <CircularProgress size={24} /> : (
          <TextField label="Flow Source Code" value={source} onChange={(e) => setSource(e.target.value)} multiline rows={12} fullWidth sx={{ fontFamily: "monospace" }} />
        )}

        {/* Inline Credential Management */}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Credentials</Typography>
        {credsLoading ? <CircularProgress size={20} /> : (
          <>
            {(assigned?.items ?? []).length === 0 && <Typography variant="body2" color="text.secondary">No credentials assigned</Typography>}
            <List dense disablePadding>
              {(assigned?.items ?? []).map((item) => {
                const cred = (allCreds?.items ?? []).find((c) => c.credentialId === item.credentialId);
                return (
                <ListItem key={item.credentialId} disableGutters>
                  <ListItemText primary={cred ? `${cred.name} (${cred.credentialType})` : item.credentialId} />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => unassignCred.mutate({ pluginId, credentialId: item.credentialId })} aria-label="Remove credential">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                );
              })}
            </List>
            {available.length > 0 && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Add credential</InputLabel>
                  <Select value={selectedCred} onChange={(e) => setSelectedCred(e.target.value)} label="Add credential">
                    {available.map((c) => (
                      <MenuItem key={c.credentialId} value={c.credentialId}>{c.name} ({c.credentialType})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="contained" size="small" disabled={!selectedCred}
                  onClick={() => { assignCred.mutate({ pluginId, credentialId: selectedCred }); setSelectedCred(""); }}>
                  Add
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={updateMeta.isPending || updateCode.isPending}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function CredentialsDialog({ pluginId, onClose }: { pluginId: string; onClose: () => void }) {
  const { data: assigned, isLoading } = usePluginCredentials(pluginId);
  const { data: allCreds } = useAdminCredentials();
  const assignCred = useAssignCredential();
  const unassignCred = useUnassignCredential();
  const [selectedCred, setSelectedCred] = useState("");

  const assignedIds = new Set((assigned?.items ?? []).map((i) => i.credentialId));
  const available = (allCreds?.items ?? []).filter((c) => !assignedIds.has(c.credentialId));

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Credentials — {pluginId}</DialogTitle>
      <DialogContent>
        {isLoading ? <CircularProgress /> : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Assigned Credentials</Typography>
            {(assigned?.items ?? []).length === 0 && <Typography color="text.secondary" sx={{ mb: 2 }}>No credentials assigned</Typography>}
            <List dense>
              {(assigned?.items ?? []).map((item) => (
                <ListItem key={item.credentialId}>
                  <ListItemText primary={item.credentialId} />
                  <ListItemSecondaryAction>
                    <IconButton size="small" onClick={() => unassignCred.mutate({ pluginId, credentialId: item.credentialId })}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            {available.length > 0 && (
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Add credential</InputLabel>
                  <Select value={selectedCred} onChange={(e) => setSelectedCred(e.target.value)} label="Add credential">
                    {available.map((c) => (
                      <MenuItem key={c.credentialId} value={c.credentialId}>{c.name} ({c.credentialType})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="contained" size="small" disabled={!selectedCred}
                  onClick={() => { assignCred.mutate({ pluginId, credentialId: selectedCred }); setSelectedCred(""); }}>
                  Add
                </Button>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
